import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Loader2, Play, X, Clock, Film, ChevronDown, Check, Maximize2, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_REFS = 30; // BytePlus hard cap for dreamina-seedance-2-5 in r2v mode (confirmed live 2026-08-07)

async function pollTask(taskId, token) {
  const maxPolls = 600;
  let notFoundCount = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/api/recipes/seedance-25-direct/task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.status === 'done') return data;
      if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      notFoundCount = 0;
    } catch (err) {
      if (err.response?.status === 404) {
        notFoundCount++;
        if (notFoundCount > 30) throw new Error('Task lost');
      } else if (err.message && !err.response) {
        throw err;
      }
    }
  }
  throw new Error('Generation timed out');
}

export default function Seedance25DirectInputs({ token, onVideoStateChange, restoredData }) {
  // refs: [{ file, preview, url }] — url set after upload
  const [refs, setRefs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('720p');

  const [generating, setGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/recipes/seedance-25-direct/runs`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => setRuns(data.runs || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!restoredData) return;
    if (restoredData.prompt) setPrompt(restoredData.prompt);
    if (restoredData.aspect_ratio) setAspectRatio(restoredData.aspect_ratio);
    if (restoredData.duration) setDuration(restoredData.duration);
    if (restoredData.resolution) setResolution(restoredData.resolution);
    const urls = restoredData.image_urls || (restoredData.image_url ? [restoredData.image_url] : []);
    if (urls.length) setRefs(urls.map(u => ({ file: null, preview: u, url: u })));
    if (restoredData.video_url) {
      setVideoUrl(restoredData.video_url);
      onVideoStateChange?.(false, restoredData.video_url);
    }
  }, [restoredData]); // eslint-disable-line

  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setRefs(prev => {
      const room = MAX_REFS - prev.length;
      const added = files.slice(0, room).map(f => ({
        file: f, preview: URL.createObjectURL(f), url: null,
      }));
      const next = [...prev, ...added];
      // Auto-set aspect ratio from the first image
      if (prev.length === 0 && added[0]) {
        const img = new Image();
        img.onload = () => setAspectRatio(img.height > img.width ? '9:16' : '16:9');
        img.src = added[0].preview;
      }
      return next;
    });
    e.target.value = '';
  };

  const removeRef = (idx) => setRefs(prev => prev.filter((_, i) => i !== idx));

  const uploadAllRefs = async () => {
    setUploading(true);
    try {
      const results = await Promise.all(refs.map(async (r) => {
        if (r.url) return r.url;
        const fd = new FormData();
        fd.append('image', r.file);
        const { data } = await axios.post(`${API}/api/recipes/seedance-25-direct/upload-image`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        });
        return data.url;
      }));
      setRefs(prev => prev.map((r, i) => ({ ...r, url: results[i] })));
      return results;
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (refs.length === 0) return alert('Please upload at least one reference image');
    if (!prompt.trim()) return alert('Please enter a prompt');

    try {
      setGenerating(true);
      setVideoUrl(null);
      try { onVideoStateChange?.(true, null, null, null); } catch {}

      const urls = await uploadAllRefs();
      if (!urls.length || urls.some(u => !u)) { alert('Reference upload failed'); setGenerating(false); return; }
      try { onVideoStateChange?.(true, null, urls[0], prompt.trim()); } catch {}

      const { data } = await axios.post(`${API}/api/recipes/seedance-25-direct/generate`, {
        image_urls: urls,
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        duration,
        resolution,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const result = await pollTask(data.task_id, token);
      if (result.video_url) {
        setVideoUrl(result.video_url);
        try { onVideoStateChange?.(false, result.video_url, null, null); } catch {}
        axios.get(`${API}/api/recipes/seedance-25-direct/runs`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data: d }) => setRuns(d.runs || [])).catch(() => {});
      }
    } catch (err) {
      alert(err.message || 'Generation failed');
      try { onVideoStateChange?.(false, null, null, null); } catch {}
    } finally {
      setGenerating(false);
    }
  };

  const loadRun = (run) => {
    if (run.inputs) {
      setPrompt(run.inputs.prompt || '');
      setAspectRatio(run.inputs.aspect_ratio || '16:9');
      setDuration(run.inputs.duration || 5);
      setResolution(run.inputs.resolution || '1080p');
      const urls = run.inputs.image_urls || (run.inputs.image_url ? [run.inputs.image_url] : []);
      setRefs(urls.map(u => ({ file: null, preview: u, url: u })));
    }
    const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
    if (videoOut) {
      setVideoUrl(videoOut.url);
      setGenerating(false);
      onVideoStateChange?.(false, videoOut.url);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="seedance-25-direct-inputs">
      {/* Reference Images (up to 30) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] text-white/40 uppercase tracking-wider">Reference Images</label>
          <span className="text-[9px] text-white/25">{refs.length}/{MAX_REFS}</span>
        </div>
        <input type="file" ref={fileInputRef} accept="image/*" multiple className="hidden" onChange={handleFilesSelect} />
        {refs.length === 0 ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-8 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]"
            style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }}
            data-testid="sd25-upload-refs-btn"
          >
            <Upload className="w-5 h-5 text-white/30" />
            <span className="text-[11px] text-white/40">Upload reference images (up to {MAX_REFS})</span>
            <span className="text-[9px] text-white/20">Characters, environments, brand assets, style guides</span>
          </button>
        ) : (
          <div className="grid grid-cols-5 gap-1.5" data-testid="sd25-refs-grid">
            {refs.map((r, i) => (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
                <img src={r.preview} alt={`ref ${i + 1}`} className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(r.preview)} />
                <button
                  onClick={() => removeRef(i)}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`sd25-remove-ref-${i}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
                <span className="absolute bottom-0.5 left-1 text-[8px] text-white/60 bg-black/50 px-1 rounded">@{i + 1}</span>
              </div>
            ))}
            {refs.length < MAX_REFS && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-lg flex items-center justify-center transition-all hover:bg-white/[0.06]"
                style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }}
                data-testid="sd25-add-ref-btn"
              >
                <Plus className="w-4 h-4 text-white/30" />
              </button>
            )}
          </div>
        )}
        {refs.length > 1 && (
          <p className="text-[9px] text-white/25 mt-1">Reference images in your prompt as @1, @2, …</p>
        )}
        {uploading && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
            <span className="text-[10px] text-white/40">Uploading references…</span>
          </div>
        )}
      </div>

      {/* Prompt */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Prompt</label>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="Describe the scene, motion and story pacing — up to 30s single-pass..."
          rows={4}
          className="w-full px-3 py-2.5 rounded-xl text-xs text-white/80 placeholder-white/20 bg-white/[0.03] border border-white/[0.08] focus:outline-none focus:border-cyan-500/30 resize-none transition-colors"
          data-testid="sd25-prompt-input"
        />
      </div>

      {/* Aspect Ratio & Resolution */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Aspect Ratio</label>
          <div className="flex gap-1.5">
            {['16:9', '9:16'].map(ar => (
              <button
                key={ar}
                onClick={() => setAspectRatio(ar)}
                className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${aspectRatio === ar ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`}
                style={{ border: '1px solid' }}
                data-testid={`sd25-ar-${ar.replace(':', '-')}-btn`}
              >
                {ar === '16:9' ? '16:9 Landscape' : '9:16 Reels'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Resolution</label>
          <div className="flex gap-1.5">
            {['720p', '1080p', '4k'].map(res => (
              <button
                key={res}
                onClick={() => setResolution(res)}
                className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${resolution === res ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`}
                style={{ border: '1px solid' }}
                data-testid={`sd25-res-${res}-btn`}
              >
                {res === '4k' ? '4K UHD' : res}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Duration (single-pass)</label>
        <div className="flex gap-1.5">
          {[5, 10, 15, 20, 25, 30].map(d => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${duration === d ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`}
              style={{ border: '1px solid' }}
              data-testid={`sd25-dur-${d}s-btn`}
            >
              {d}s
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generating || refs.length === 0 || !prompt.trim()}
        className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2"
        style={{
          background: generating ? 'rgba(14,165,233,0.12)' : 'linear-gradient(135deg, #0EA5E9, #7C3AED)',
          boxShadow: generating ? 'none' : '0 4px 16px rgba(14,165,233,0.25)',
        }}
        data-testid="sd25-generate-btn"
      >
        {generating ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Generating {duration}s {resolution === '4k' ? '4K' : resolution} Video...</>
        ) : (
          <><Play className="w-4 h-4" /> Generate Video</>
        )}
      </button>

      {/* Recent Runs */}
      {runs.length > 0 && (
        <div data-testid="sd25-recent-runs">
          <button
            onClick={() => setRunsOpen(!runsOpen)}
            className="flex items-center justify-between w-full py-1.5 mb-1"
          >
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-white/30" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Recent Runs</span>
              <span className="text-[9px] text-white/20 bg-white/[0.05] px-1.5 py-0.5 rounded-full">{runs.length}</span>
            </div>
            <ChevronDown className={`w-3 h-3 text-white/20 transition-transform ${runsOpen ? 'rotate-180' : ''}`} />
          </button>
          {runsOpen && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="sd25-runs-list">
              {runs.map(run => {
                const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
                const isActive = videoOut && videoUrl === videoOut.url;
                const firstRef = run.inputs?.image_urls?.[0] || run.inputs?.image_url;
                return (
                  <button
                    key={run.run_id}
                    onClick={() => loadRun(run)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-all ${isActive ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'}`}
                    style={{ border: '1px solid' }}
                    data-testid={`sd25-run-${run.run_id}`}
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
                      {firstRef ? (
                        <img src={firstRef} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-4 h-4 text-white/20" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-white/60 truncate">{run.inputs?.prompt || 'No prompt'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] font-medium ${run.status === 'completed' ? 'text-emerald-400/70' : run.status === 'failed' ? 'text-red-400/70' : 'text-amber-400/70'}`}>
                          {run.status === 'completed' ? 'Done' : run.status === 'failed' ? 'Failed' : 'Processing'}
                        </span>
                        <span className="text-[9px] text-white/20">{run.inputs?.duration || 5}s</span>
                        <span className="text-[9px] text-white/20">{run.inputs?.resolution || '1080p'}</span>
                        <span className="text-[9px] text-white/20">{(run.inputs?.image_urls || []).length || 1} refs</span>
                        <span className="text-[9px] text-white/15">{timeAgo(run.started_at)}</span>
                      </div>
                    </div>
                    {isActive && <Check className="w-3 h-3 text-cyan-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Reference" className="max-w-full max-h-full rounded-xl" />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
